/**
 * Google Tag Manager, carried over from the legacy frontend (`_legacy/vv-frontend`)
 * with the same container and the same dataLayer events.
 *
 * GTM loads on every visit, after the Consent Mode defaults are set
 * (`cookie-consent/consent-mode.ts`). Everything it runs — Analytics included —
 * is configured in the container, which decides per tag what consent allows;
 * the app loads no tracking script of its own.
 *
 * Enabled by `VITE_GTM_ID`. Unset — local development, the dev stage, tests —
 * GTM does not load, so test traffic never reaches the production container.
 */

declare global {
  interface Window {
    /** Event objects, plus `arguments` objects for Consent Mode commands. */
    dataLayer?: (Record<string, unknown> | IArguments)[];
  }
}

const GTM_ID = import.meta.env.VITE_GTM_ID?.trim();

function pushDataLayer(entry: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(entry);
}

/** Runs once at startup, after the Consent Mode defaults. */
export function loadTagManager(gtmId: string | undefined = GTM_ID): void {
  if (gtmId === undefined || gtmId === '') {
    return;
  }

  pushDataLayer({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(script);
}

/**
 * Legacy `JobDetails.handleApply`. Always pushed: whether a tag may act on it
 * is GTM's decision under Consent Mode.
 */
export function trackApply(): void {
  pushDataLayer({ event: 'apply' });
}

/**
 * Legacy `Giveaway` component: a "Lov na blago" icon appeared on a stand. Pushed
 * once per appearance; the legacy component pushed it on every re-render.
 */
export function trackTreasureHuntAppear(): void {
  pushDataLayer({ event: 'treasure_hunt_appear' });
}

/** Legacy `Giveaway.handleClick`: the visitor caught the icon. */
export function trackTreasureHuntClick(): void {
  pushDataLayer({ event: 'treasure_hunt_click' });
}

export type ShareContentType = 'exhibitor' | 'job';

/**
 * New with this app; the legacy frontend had no share button. Shaped as GA4's
 * recommended `share` event, so a GA4 tag can forward the fields as they are.
 * Pushed when the share sheet opens (or the link is copied, where there is no
 * sheet): browsers do not say which app, if any, the visitor then picked.
 */
export function trackShare(share: {
  contentType: ShareContentType;
  itemId: string;
  method: 'native' | 'copy_link';
}): void {
  pushDataLayer({
    event: 'share',
    method: share.method,
    content_type: share.contentType,
    item_id: share.itemId,
  });
}
